import {
  Activity,
  AirVent,
  AlarmClock,
  AlarmSmoke,
  Anchor,
  Apple,
  Armchair,
  Award,
  Axe,
  Backpack,
  Battery,
  BatteryCharging,
  Bath,
  Bed,
  Beef,
  BellRing,
  Bike,
  Boxes,
  BrickWall,
  Briefcase,
  Brush,
  Bug,
  CableCar,
  Camera,
  Candy,
  Car,
  Carrot,
  Cat,
  CheckCircle2,
  ChefHat,
  CircleAlert,
  Citrus,
  Clipboard,
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Coffee,
  Coins,
  Compass,
  Construction,
  CookingPot,
  Crown,
  CupSoda,
  Cylinder,
  Dog,
  Droplet,
  Droplets,
  Dumbbell,
  Egg,
  Fan,
  Feather,
  Fence,
  Film,
  Filter,
  FishSymbol,
  Flame,
  FlaskConical,
  Flower,
  Flower2,
  Footprints,
  Forklift,
  Frame,
  Fuel,
  Gamepad2,
  Gauge,
  Gem,
  Gift,
  GlassWater,
  Hammer,
  HandHelping,
  Headphones,
  Heart,
  Home,
  House,
  IceCream,
  Key,
  KeyRound,
  Lamp,
  Laptop,
  Layers,
  Leaf,
  LeafyGreen,
  LifeBuoy,
  Lightbulb,
  Link,
  LockKeyhole,
  Mailbox,
  MapPin,
  Microwave,
  Milk,
  Mountain,
  Mouse,
  Music,
  Package,
  PaintBucket,
  Paintbrush,
  PartyPopper,
  PawPrint,
  PencilRuler,
  PersonStanding,
  PiggyBank,
  Pin,
  Pizza,
  Plane,
  PlugZap,
  Popcorn,
  Power,
  Printer,
  Recycle,
  Refrigerator,
  RotateCcw,
  RotateCw,
  Route,
  Rss,
  Ruler,
  Salad,
  Sandwich,
  Save,
  Scissors,
  Search,
  SearchCheck,
  Settings,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  ShipWheel,
  Shovel,
  ShowerHead,
  Skull,
  Snowflake,
  Sofa,
  Soup,
  Sparkle,
  Sparkles,
  Speaker,
  Spool,
  Sprout,
  Square,
  SquareDashed,
  SquareDashedBottom,
  Stamp,
  Star,
  Stethoscope,
  Sun,
  Sunrise,
  Sunset,
  TableProperties,
  Target,
  Tent,
  TestTube,
  Thermometer,
  ThumbsUp,
  Tractor,
  TrafficCone,
  TrainTrack,
  Trash2,
  TreeDeciduous,
  TreePine,
  Trees,
  Trophy,
  Truck,
  Tv,
  Umbrella,
  Utensils,
  UtensilsCrossed,
  Waves,
  Webhook,
  Wheat,
  Wind,
  Wine,
  Wrench,
  Zap,
} from 'lucide-react-native';
import type { ComponentType } from 'react';

type IconProps = { size?: number; color?: string; strokeWidth?: number };

/**
 * String-keyed icon registry. Task rules in JSON and user-created custom
 * tasks reference icons by name. Add new icons here as needs arise — lucide
 * has 1500+ available, we just need to import them.
 */
export const ICONS: Record<string, ComponentType<IconProps>> = {
  Activity, AirVent, AlarmClock, AlarmSmoke, Anchor, Apple, Armchair, Award, Axe,
  Backpack, Battery, BatteryCharging, Bath, Bed, Beef, BellRing, Bike,
  Box: Boxes, Boxes, BrickWall, Briefcase, Brush, Bug,
  CableCar, Camera, Candy, Car, Carrot, Cat, CheckCircle2, ChefHat,
  CircleAlert, Citrus, Clipboard, Cloud, CloudFog, CloudLightning, CloudRain,
  CloudSnow, CloudSun, Coffee, Coins, Compass, Construction, CookingPot,
  Crown, CupSoda, Cylinder,
  Dog, Droplet, Droplets, Dumbbell,
  Egg,
  Fan, Feather, Fence, Film, Filter, FishSymbol, Flame, FlaskConical,
  Flower, Flower2, Footprints, Forklift, Frame, Fuel,
  Gamepad2, Gauge, Gem, Gift, GlassWater,
  Hammer, HandHelping, Headphones, Heart, Home, House,
  IceCream,
  Key, KeyRound,
  Lamp, Laptop, Layers, Leaf, LeafyGreen, LifeBuoy, Lightbulb, Link, LockKeyhole,
  Mailbox, MapPin, Microwave, Milk, Mountain, Mouse, Music,
  Package, PaintBucket, Paintbrush, PartyPopper, PawPrint, PencilRuler,
  PersonStanding, PiggyBank, Pin, Pizza, Plane, PlugZap, Popcorn, Power, Printer,
  Recycle, Refrigerator, RotateCcw, RotateCw, Route, Rss, Ruler,
  Salad, Sandwich, Save, Scissors, Search, SearchCheck, Settings, Settings2,
  ShieldAlert, ShieldCheck, ShipWheel, Shovel, ShowerHead, Skull, Snowflake,
  Sofa, Soup, Sparkle, Sparkles, Speaker, Spool, Sprout, Square,
  SquareDashed, SquareDashedBottom, Stamp, Star, Stethoscope, Sun, Sunrise,
  Sunset,
  TableProperties, Target, Tent, TestTube, Thermometer, ThumbsUp, Tractor,
  TrafficCone, TrainTrack, Trash2, TreeDeciduous, TreePine,
  Trees, Trophy, Truck, Tv,
  Umbrella, Utensils, UtensilsCrossed,
  Waves, Webhook, Wheat, Wind, Wine, Wrench,
  Zap,
};

export function resolveIcon(name: string | null | undefined): ComponentType<IconProps> {
  if (!name) return ICONS.Sparkles;
  return ICONS[name] ?? ICONS.Sparkles;
}

/**
 * Curated icon picker for custom tasks. Grouped + much larger than v1 so
 * users can find something that feels right. Order matters here — most-used
 * categories first.
 */
export const CUSTOM_TASK_ICONS: { group: string; keys: string[] }[] = [
  {
    group: 'General',
    keys: ['Sparkles', 'CheckCircle2', 'Target', 'Star', 'PartyPopper', 'Trophy', 'Award', 'Heart', 'ThumbsUp', 'Sparkle'],
  },
  {
    group: 'Tools',
    keys: ['Wrench', 'Hammer', 'Settings2', 'PencilRuler', 'Ruler', 'Scissors', 'Brush', 'Paintbrush', 'PaintBucket', 'Save'],
  },
  {
    group: 'Mechanical',
    keys: ['Filter', 'Gauge', 'AirVent', 'Fan', 'Thermometer', 'PlugZap', 'Power', 'Cylinder', 'Fuel'],
  },
  {
    group: 'Water',
    keys: ['Droplets', 'Droplet', 'ShowerHead', 'GlassWater', 'Waves', 'FlaskConical', 'TestTube'],
  },
  {
    group: 'Outdoor & yard',
    keys: ['Leaf', 'LeafyGreen', 'Sprout', 'TreePine', 'TreeDeciduous', 'Trees', 'Flower', 'Flower2', 'Wheat', 'Sun', 'Sunrise', 'Sunset'],
  },
  {
    group: 'Kitchen & food',
    keys: ['CookingPot', 'Utensils', 'UtensilsCrossed', 'ChefHat', 'Coffee', 'Refrigerator', 'Microwave', 'Apple', 'Carrot', 'Citrus', 'Salad', 'Soup', 'Pizza', 'Sandwich', 'Egg', 'Milk', 'Wine'],
  },
  {
    group: 'Safety & home',
    keys: ['AlarmSmoke', 'AlarmClock', 'ShieldCheck', 'ShieldAlert', 'CircleAlert', 'Home', 'House', 'Bed', 'Bath', 'Sofa', 'Armchair', 'Lamp', 'Lightbulb', 'Key', 'KeyRound', 'LockKeyhole'],
  },
  {
    group: 'Weather & seasons',
    keys: ['Snowflake', 'Cloud', 'CloudRain', 'CloudSnow', 'CloudSun', 'CloudLightning', 'CloudFog', 'Umbrella'],
  },
  {
    group: 'Vehicles & equipment',
    keys: ['Tractor', 'Truck', 'Car', 'Bike', 'Forklift', 'TrafficCone'],
  },
  {
    group: 'Pets & life',
    keys: ['PawPrint', 'Dog', 'Cat', 'Bug', 'Mailbox', 'Gift', 'PiggyBank', 'Coins', 'Music', 'Gamepad2', 'Tv', 'Camera', 'Printer'],
  },
  {
    group: 'Energy',
    keys: ['Zap', 'BatteryCharging', 'Battery', 'Sun', 'Recycle'],
  },
];
